import React, { useEffect, useState } from "react";
import {
  FieldV2,
  SchemaFactoryV2,
  BaseFieldProps,
  CoreFieldValue,
  AsyncODataFieldProps,
  type Option,
} from "@ucl/library";
import { get } from "lodash";
import { TaskExecutionTypeFieldSchema } from "@bolt/core";

export const TaskExecutionTypeFieldSets = {
  TaskExecutionTypeContext: "TaskExecutionTypeContext",
  TaskExecutionTypeField: "TaskExecutionTypeField",
} as const;

export type TaskExecutionTypeFieldSets =
  (typeof TaskExecutionTypeFieldSets)[keyof typeof TaskExecutionTypeFieldSets];

interface TaskExecutionTemplateFieldsComponentProps {
  taskExecutionType: string;
  entityType: string;
  fieldSet: TaskExecutionTypeFieldSets;
  taskExecutionTypeData?: TaskExecutionTypeFieldSchema;
  factory: SchemaFactoryV2;
  getAuthRequestHeader: () => Promise<Record<string, string> | undefined>;
  apiBaseUrl: string;
  errors?: { [key: string]: string[] };
  updateTaskExecutionTypeData?: (
    updatedModel: TaskExecutionTypeFieldSchema
  ) => void;
  isReadOnly?: boolean;
}

export const TaskExecutionTemplateFieldsComponent: React.FC<
  TaskExecutionTemplateFieldsComponentProps
> = (props) => {
  useEffect(() => {
    setupFields();
  }, []);

  const [fields, setFields] =
    useState<
      Record<
        string,
        BaseFieldProps<CoreFieldValue> & Partial<AsyncODataFieldProps>
      >
    >();

  const setupFields = () => {
    const schemaFields = props.factory.buildModelSchema(
      props.taskExecutionType,
      (_, newFieldValue, fieldProps) => {
        // Resolve Value
        let valueProps: BaseFieldProps<CoreFieldValue> &
          Partial<AsyncODataFieldProps> = fieldProps;
        let value = newFieldValue;

        // OData Value Resolver
        if (valueProps.oDataKey) {
          if (valueProps.isValueMultiple) {
            value = (value as Option<string>[]).map(
              (item: Option<string>) => item.value
            );
          } else {
            // If value is an object, extract the value
            if (typeof value === "object") {
              value = (value as Option<string>).value;
            } else {
              value = value as string;
            }
          }
        }

        // Update Task Execution Type Data
        const fieldSchemaKeyValuePair: TaskExecutionTypeFieldSchema = {
          [fieldProps.fieldKey as string]: String(value),
        };

        props.updateTaskExecutionTypeData?.(fieldSchemaKeyValuePair);
      },
      props.taskExecutionType,
      props.errors,
      undefined,
      undefined
    );

    const resolvedFields: Record<
      string,
      BaseFieldProps<CoreFieldValue> & Partial<AsyncODataFieldProps>
    > = {};

    schemaFields.forEach((field) => {
      let fieldProps: BaseFieldProps<CoreFieldValue> &
        Partial<AsyncODataFieldProps> = field;

      if (fieldProps.oDataKey) {
        fieldProps = {
          ...field,
          baseODataUrl: `${props.apiBaseUrl}/bolt/odata`,
          suffixODataUrl: `?$filter=(entityType eq '${props.entityType}' or entityType eq null)`,
          getAuthRequestHeader: props.getAuthRequestHeader,
          textColumnName: "label",
          valueColumnName: "value",
        };
      }

      if (field.fieldSet !== props.fieldSet) {
        fieldProps.visible = false;
      }

      if (props.isReadOnly) {
        fieldProps.disabled = true;
      }

      if (props.taskExecutionTypeData) {
        fieldProps.value = get(
          props.taskExecutionTypeData,
          field.fieldKey as string
        );

        if (
          fieldProps.oDataKey &&
          fieldProps.value &&
          fieldProps.isValueMultiple
        ) {
          // Split value by comma
          fieldProps.value = fieldProps.value.split(",");
        }
      }

      resolvedFields[field.fieldKey as string] = fieldProps;
    });

    setFields(resolvedFields);
  };

  if (!fields) {
    return <></>;
  }

  return Object.keys(fields).map((fieldKey) => {
    return (
      <FieldV2
        key={`${props.taskExecutionType}-${fieldKey}`}
        fieldProps={fields[fieldKey]}
      />
    );
  });
};
